/**
 * Homepage Content
 *
 * All section text lives here. Edit copy without touching components.
 */

export const heroContent = {
    badge: "Pre Alpha // In Active Development",
    headline: "The Year Is 2145.\nEarth Has Failed.",
    subheadline:
        "There is no rescue coming. The only option left is a planet that has broken every expedition before yours. This is Expedition 744.",
    scrollLabel: "Begin the Story",
};

export const storyContent = {
    chapter1: {
        eyebrow: "Chapter I // The Collapse",
        headline: "They Warned Us. We Did Not Listen.",
        paragraphs: [
            "By 2099 the warnings had been mounting for decades. Climate breakdown, biosphere depletion, cascading resource shortages. Governments stalled. Corporations looked the other way. But the data kept pointing to the same conclusion.",
            "By 2145 the Earth had crossed a point of no return. Agriculture collapsed across multiple continents. Entire ocean systems destabilised. Whole regions became uninhabitable. The planet that had sustained us for millennia could no longer keep us alive.",
            "What began as a research programme was reborn as an emergency survival project. Its name was the GreenMars Initiative, and its purpose was no longer to study Mars. It was to make it liveable before time ran out.",
        ],
    },
    chapter2: {
        eyebrow: "Chapter II // The Expeditions",
        headline: "743 Missions Sent Before Yours. Almost None Survived.",
        paragraphs: [
            "Over the decades that followed, humanity poured its remaining industrial strength into Mars. Earlier expeditions built mining outposts, automated laboratories, and partial landing infrastructure. Every mission relied on whatever the previous one had left behind.",
            "The record, however, is catastrophic. Contact has been lost with nearly every crewed expedition. Some bases transmitted for months before falling silent. Others vanished during descent.",
            "One crew did return, in an emergency state. Their vessel was badly damaged, and only a single fragment of the debrief was ever made public:",
        ],
        quote: '"We are all lost. Never send anyone back to that cursed planet."',
        closing: "In spite of everything, Mars remains the best option humanity has left. And you are next.",
    },
    chapter3: {
        eyebrow: "Chapter III // Your Mission",
        headline: "You Do Not Explore Mars. You Rewrite It.",
        description:
            "Expedition 744 is not an exploration mission. It is a full scale planetary conversion mission. You bring engineers, scientists, field operators, automated construction systems, and the most advanced infrastructure package ever assembled for off world terraformation.",
        objectives: [
            "Establish a permanent industrial foothold on the surface",
            "Industrialize the surface and sustain every system the mission demands",
            "Alter the atmosphere, temperature, and magnetic shielding of an entire planet",
            "Seed biological systems and begin the ecological conversion of Mars",
        ],
    },
};

export const pillarsContent = {
    eyebrow: "The Three Pillars",
    headline: "One Mission. Three Fronts.",
    description:
        "Mars cannot be transformed by a single system. The mission rests on three interconnected pillars, each one fragile on its own, but together capable of bending a dead world towards life.",
    pillars: [
        {
            number: "01",
            title: "Industrial Expansion",
            subtitle: "Build the Machine",
            description:
                "Power generation, mining, fabrication, logistics, launch systems. Every machine on Mars must be built by your hands. Without industry, nothing else can move.",
            icon: "industry",
        },
        {
            number: "02",
            title: "Planetary Engineering",
            subtitle: "Reshape the World",
            description:
                "Deploy megastructures that change Mars at a planetary scale. Orbital engines to shift its path around the Sun. Magnetic field networks to shield the atmosphere from solar stripping.",
            icon: "planet",
        },
        {
            number: "03",
            title: "Ecological Conversion",
            subtitle: "Bring Mars to Life",
            description:
                "Process the atmosphere, liberate water, condition the soil, seed microbial life, and spread vegetation. Terraforming is not a switch you flip. It snowballs.",
            icon: "ecology",
        },
    ],
};

export const threatsContent = {
    eyebrow: "The Opposition",
    headline: "Mars Does Not Want You Here.",
    description:
        "The planet pushes back at every level. Extreme cold. Lethal radiation. Dust storms. Atmospheric instability. And something else entirely. Unknown life has adapted to Martian conditions in ways no one predicted. It is territorial, resilient, and hostile.",
};

export const trailerContent = {
    eyebrow: "See It in Motion",
    headline: "Watch the Trailer",
};

export const mediaContent = {
    eyebrow: "From the Surface",
    headline: "Pre Alpha Transmissions",
    description:
        "Raw footage from the development front. Every screenshot is real, every system is being built. This is Expedition 744 taking shape.",
    items: [
        {
            src: "/media/Screenshot_PreAlpha_1.webp",
            alt: "Expedition 744 pre alpha, industrial systems",
            caption: "First industrial outpost established",
        },
        {
            src: "/media/Screenshot_PreAlpha_2.webp",
            alt: "Expedition 744 pre alpha, Martian terrain",
            caption: "The Martian surface stretches endlessly",
        },
        {
            src: "/media/Screenshot_PreAlpha_3.webp",
            alt: "Expedition 744 pre alpha, resource extraction",
            caption: "Resource extraction operations begin",
        },
        {
            src: "/media/Screenshot_PreAlpha_4.webp",
            alt: "Expedition 744 pre alpha, base construction",
            caption: "Base expansion in hostile conditions",
        },
        {
            src: "/media/Screenshot_PreAlpha_5.webp",
            alt: "Expedition 744 pre alpha, planetary systems",
            caption: "Monitoring planetary conditions",
        },
        {
            src: "/media/Screenshot_PreAlpha_6.webp",
            alt: "Expedition 744 pre alpha, manufacturing",
            caption: "Manufacturing chains coming online",
        },
        {
            src: "/media/Screenshot_PreAlpha_7.webp",
            alt: "Expedition 744 pre alpha, terraforming",
            caption: "The first signs of change",
        },
    ],
};

export const wishlistContent = {
    eyebrow: "Join the Mission",
    headline: "The Expedition Needs You",
    description:
        "Expedition 744 is being built right now. Wishlisting on Steam is the single most impactful thing you can do. It tells the world that this mission matters.",
    reasons: [
        "Get notified the moment the demo is available",
        "Directly boost the game visibility on Steam",
        "Support an indie studio and help keep the project alive",
    ],
    ctaLabel: "Wishlist on Steam",
};

export const newsletterContent = {
    eyebrow: "Stay Connected",
    headline: "Receive Transmissions",
    description:
        "Subscribe for development updates from the surface. No spam, only genuine milestones and progress reports.",
    placeholder: "your@email.com",
    ctaLabel: "Subscribe",
    successMessage: "Check your inbox - a confirmation link is on its way.",
    successDetail:
        "Click the link in the email to activate your subscription. The link expires in 24 hours.",
    spamNote:
        "Don't see the email? Check your spam or junk folder just in case.",
    confirmedMessage: "Transmission link established. Updates will follow.",
    reassurance: "No spam. Unsubscribe anytime. Only meaningful updates.",
};

export const footerContent = {
    closingLine: "Expedition 744. A 744 Interactive project, built with conviction.",
    copyright: `© ${new Date().getFullYear()} 744 Interactive. All rights reserved.`,
};
